/* eslint-disable @typescript-eslint/no-explicit-any */
import type { AppProps } from 'next/app';
import { I18nProvider } from 'next-localization';
import { SitecorePageProps } from 'lib/page-props';
import Bootstrap from 'src/Bootstrap';
import { ThemeContext } from 'lib/context/ThemeContext';
import { getTheme } from 'lib/multisite/theme-mapping';
import '../assets/customCalender.css';
import '../assets/app.css';
import '../assets/typography.css';
import { defaultConfig } from 'tailwind-variants';
import { CookiesProvider } from 'react-cookie';
import { useEffect } from 'react';
import '../assets/mobileApp.css';
import { MobileProvider, useMobile } from 'lib/context/MobileContext';
import { storageKeys } from 'src/helpers/mobileConstants';
// prevents to override tailwind classes.
defaultConfig.twMerge = false;

//declare the window object for the flutter webview communication across the app
declare global {
  interface Window {
    flutter_inappwebview?: {
      callHandler: (handlerName: any, ...args: any[]) => void;
    };
    receiveFromFlutter?: (data: any) => void;
    receiveTokenFromFlutter?: (data: any) => void;
  }
}

function AppContent({ Component, pageProps }: AppProps<SitecorePageProps>): JSX.Element {
  const { dictionary, ...rest } = pageProps;
  const site = pageProps.site;
  const { updateMobileData } = useMobile();

  //check if the user is on a flutter webview
  useEffect(() => {
    if (typeof navigator !== 'undefined') {
      const userAgent = navigator.userAgent || '';
      const isFlutterWebView = /PSP_MOBILE_APP/i.test(userAgent);
      //if the user is on a flutter webview, update the mobile data and add the flutterWebview class to the body
      if (isFlutterWebView) {
        updateMobileData({ isFlutterWebView: true });
        localStorage.setItem(storageKeys.IS_FLUTTER_WEB_VIEW, 'true');
        //add the flutterWebview class to the body to handle the UI changes
        document.body.classList.add('flutterWebview');
      }
    }
  }, []);

  //receive messages from the flutter webview and update the mobile data
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Set up the receiveFromFlutter function
      window.receiveFromFlutter = function (data: any) {
        updateMobileData(data);
      };
      // Set up the receiveTokenFromFlutter function
      window.receiveTokenFromFlutter = function (data: any) {
        updateMobileData(data);
      };
    }
    return () => {
      window.receiveFromFlutter = undefined;
      window.receiveTokenFromFlutter = undefined;
    };
  }, []);

  return (
    <>
      <Bootstrap {...pageProps} />
      {/*
        // Use the next-localization (w/ rosetta) library to provide our translation dictionary to the app.
        // Note Next.js does not (currently) provide anything for translation, only i18n routing.
        // If your app is not multilingual, next-localization and references to it can be removed.
      */}
      <I18nProvider lngDict={dictionary} locale={pageProps.locale}>
        <ThemeContext.Provider value={getTheme(site)}>
          <CookiesProvider defaultSetOptions={{ path: '/' }}>
            <Component {...rest} />
          </CookiesProvider>
        </ThemeContext.Provider>
      </I18nProvider>
    </>
  );
}

//wrap the app in the mobile context provider
function App(props: AppProps<SitecorePageProps>): JSX.Element {
  return (
    <MobileProvider>
      <AppContent {...props} />
    </MobileProvider>
  );
}

export default App;
